import { useCallback, useMemo, useState } from "react";
import { AlertTriangle, Loader2, PackageCheck, PackageX, Undo2 } from "lucide-react";

import { createClientRequestId } from "../../utils/clientRequestId";
import { riderApi } from "../services/riderApi";

// WP7 Parcel return and Emergency Recovery, Rider side (target-v2).
//
// The screen exists to make one boundary obvious: presenting is not returning.
// The Rider names the exact Parcels they are carrying to the counter, and that is
// all that happens. Staff confirm what they physically take, on their own screen,
// and until they do every Parcel here is still the Rider's accountability.
//
// There is deliberately no "mark as returned" control, no receiver field and no
// way to record a handover from this side. A Rider who could do any of those
// could discharge their own custody, which is the one thing the two-party
// protocol exists to prevent.

const EMERGENCY_REASONS = Object.freeze([
  ["VEHICLE_INOPERABLE", "My vehicle cannot continue"],
  ["MEDICAL_EMERGENCY", "Medical emergency"],
  ["SAFETY_EMERGENCY", "Safety emergency"],
  ["DEVICE_FAILURE", "My device failed"],
  ["RIDER_UNABLE_TO_CONTINUE", "I cannot continue the trip"],
  ["OTHER", "Something else"],
]);

const DISPOSITION_LABEL = Object.freeze({
  RETURN_REQUIRED: "Dispatch asked for this back",
  RESCHEDULE_OUTSIDE_RUN: "Rescheduled outside this trip",
  MANAGEMENT_REVIEW: "Waiting on management review",
});

export default function RiderParcelReturnPanel({
  businessId,
  heldParcels = [],
  openSession = null,
  disabled = false,
  initialMode = "return",
  onChanged,
}) {
  const [selected, setSelected] = useState(() => new Set());
  const [mode, setMode] = useState(initialMode === "emergency" ? "emergency" : "return");
  const [emergencyReason, setEmergencyReason] = useState("VEHICLE_INOPERABLE");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const runId = useMemo(() => {
    const ids = new Set(heldParcels.map((parcel) => Number(parcel.deliveryRunId)).filter(Boolean));
    return ids.size === 1 ? [...ids][0] : null;
  }, [heldParcels]);

  const toggle = useCallback((shipmentId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(shipmentId)) next.delete(shipmentId);
      else next.add(shipmentId);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(heldParcels.map((parcel) => Number(parcel.shipmentId))));
  }, [heldParcels]);

  const run = async (label, work) => {
    setBusy(label);
    setError("");
    try {
      const result = await work(createClientRequestId(`parcel_return_${label}`));
      setSelected(new Set());
      setNote("");
      onChanged?.(result);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message || requestError.message || "Something went wrong.",
      );
    } finally {
      setBusy("");
    }
  };

  if (!heldParcels.length && !openSession) return null;

  const emergency = mode === "emergency";
  const needsNote = emergency && emergencyReason === "OTHER";
  const canPresent = Boolean(runId) && selected.size > 0 && !disabled && !busy
    && (!needsNote || note.trim().length >= 10);

  return (
    <section className="rounded-3xl border border-stone-300 bg-white p-5 dark:border-white/15 dark:bg-neutral-900">
      <h3 className="flex items-center gap-2 text-lg font-semibold text-stone-950 dark:text-white">
        <PackageX className="h-5 w-5" aria-hidden="true" />
        Parcels you are still carrying
      </h3>

      {openSession ? (
        <div className="mt-4 rounded-2xl border border-sky-300 bg-sky-50 p-4 dark:border-sky-500/30 dark:bg-sky-950/20">
          <p className="font-semibold text-stone-950 dark:text-white">
            {openSession.sessionKind === "EMERGENCY_RECOVERY"
              ? "Emergency handover waiting at the counter"
              : "Return waiting at the counter"}
          </p>
          <p className="mt-1 text-sm text-stone-700 dark:text-stone-300">
            You presented {openSession.presentedCount}{" "}
            {openSession.presentedCount === 1 ? "parcel" : "parcels"}. Staff have to scan
            and confirm each one. Nothing has left your accountability yet.
          </p>
          <button
            type="button"
            disabled={disabled || Boolean(busy)}
            onClick={() => run("abandon", (idempotencyKey) => riderApi.abandonReturnSession(
              businessId, openSession.returnSessionId,
              { expectedVersion: openSession.version, idempotencyKey },
            ))}
            className="mt-4 flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-stone-300 px-4 font-semibold text-stone-800 disabled:opacity-50 dark:border-white/20 dark:text-stone-100"
          >
            {busy === "abandon"
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : <Undo2 className="h-4 w-4" aria-hidden="true" />}
            Withdraw what I presented
          </button>
        </div>
      ) : (
        <>
          <p className="mt-1 text-sm text-stone-700 dark:text-stone-300">
            Presenting is not returning. Choose what you are physically carrying to the
            counter; staff confirm each parcel when they take it.
          </p>

          <div className="mt-4 flex gap-2" role="group" aria-label="Kind of handover">
            <button
              type="button"
              onClick={() => setMode("return")}
              aria-pressed={!emergency}
              className={`min-h-11 flex-1 rounded-2xl px-3 text-sm font-semibold ${
                emergency
                  ? "border border-stone-300 text-stone-700 dark:border-white/20 dark:text-stone-200"
                  : "bg-stone-900 text-white dark:bg-white dark:text-stone-900"
              }`}
            >
              Returning parcels
            </button>
            <button
              type="button"
              onClick={() => setMode("emergency")}
              aria-pressed={emergency}
              className={`min-h-11 flex-1 rounded-2xl px-3 text-sm font-semibold ${
                emergency
                  ? "bg-amber-700 text-white"
                  : "border border-stone-300 text-stone-700 dark:border-white/20 dark:text-stone-200"
              }`}
            >
              I cannot continue
            </button>
          </div>

          {emergency ? (
            <div className="mt-4 rounded-2xl bg-amber-50 p-4 dark:bg-amber-950/20">
              <label className="block text-sm font-semibold text-stone-950 dark:text-white">
                What stopped you?
                <select
                  value={emergencyReason}
                  onChange={(event) => setEmergencyReason(event.target.value)}
                  className="mt-2 min-h-12 w-full rounded-2xl border border-stone-300 bg-white px-3 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white"
                >
                  {EMERGENCY_REASONS.map(([value, text]) => (
                    <option key={value} value={value}>{text}</option>
                  ))}
                </select>
              </label>
              <p className="mt-3 text-sm text-stone-700 dark:text-stone-300">
                This is recorded as an emergency handover, not as a failed delivery. The
                customer does not lose a delivery attempt because of it.
              </p>
            </div>
          ) : null}

          <ul className="mt-4 space-y-2">
            {heldParcels.map((parcel) => {
              const shipmentId = Number(parcel.shipmentId);
              return (
                <li key={shipmentId}>
                  <label className="flex min-h-14 items-center gap-3 rounded-2xl border border-stone-300 px-4 dark:border-white/15">
                    <input
                      type="checkbox"
                      checked={selected.has(shipmentId)}
                      onChange={() => toggle(shipmentId)}
                      className="h-5 w-5"
                    />
                    <span className="flex-1">
                      <span className="block font-semibold text-stone-950 dark:text-white">
                        {parcel.orderReference || `Parcel ${shipmentId}`}
                      </span>
                      <span className="block text-sm text-stone-600 dark:text-stone-300">
                        {DISPOSITION_LABEL[parcel.dispositionCode] || "Still in your custody"}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>

          {heldParcels.length > 1 ? (
            <button
              type="button"
              onClick={selectAll}
              className="mt-3 text-sm font-semibold text-sky-800 underline dark:text-sky-300"
            >
              Select every parcel I am carrying
            </button>
          ) : null}

          {needsNote ? (
            <label className="mt-4 block text-sm font-semibold text-stone-950 dark:text-white">
              Tell the business what happened
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={3}
                maxLength={500}
                className="mt-2 w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 font-normal text-stone-950 dark:border-white/15 dark:bg-neutral-900 dark:text-white"
              />
            </label>
          ) : null}

          {!runId && heldParcels.length ? (
            <p className="mt-4 flex items-start gap-2 rounded-2xl bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              These parcels belong to different trips. Hand back one trip at a time.
            </p>
          ) : null}

          <button
            type="button"
            disabled={!canPresent}
            onClick={() => run(emergency ? "emergency" : "present", (idempotencyKey) => (emergency
              ? riderApi.openEmergencyRecovery(businessId, {
                deliveryRunId: runId,
                presentedShipmentIds: [...selected],
                emergencyReasonCode: emergencyReason,
                note: note.trim() || undefined,
                idempotencyKey,
              })
              : riderApi.openReturnSession(businessId, {
                deliveryRunId: runId,
                presentedShipmentIds: [...selected],
                note: note.trim() || undefined,
                idempotencyKey,
              })))}
            className="mt-4 flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-stone-900 px-5 text-lg font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-stone-900"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              : <PackageCheck className="h-5 w-5" aria-hidden="true" />}
            {emergency
              ? `Hand over ${selected.size || ""} at the counter`.replace("  ", " ")
              : `Present ${selected.size || ""} for return`.replace("  ", " ")}
          </button>
        </>
      )}

      {error ? (
        <p role="alert" className="mt-4 flex items-start gap-2 rounded-2xl bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </section>
  );
}
